// Blocklist controls for a client edit page (person or company), as a hook so
// the page can place each piece where it belongs:
//
//   const block = useClientBlock({ entityType: "customer", entityId, name, onChange });
//   <Page actionGroups={block.actionGroups} primaryAction={…disabled={block.readOnly}}>
//     {block.markup}
//     <fieldset disabled={block.readOnly}> …the form… </fieldset>
//   </Page>
//
// Blocking is deliberately low-key — it lives inside the "Más acciones" menu.
// A *blocked* client is the loud state: a critical banner at the top of the page.
//
// Blocking is open to everyone; undoing it is not. Unblocking — and editing a
// blocked client at all — needs the `clients.unblock` permission, enforced for
// real by the `enforce_client_block_authority` trigger on customers/companies.

import React, { useCallback, useEffect, useState } from "react";
import {
  Banner,
  BlockStack,
  Button,
  FormLayout,
  InlineStack,
  Modal,
  Select,
  Text,
  TextField,
} from "@shopify/polaris";
import { UserAuth } from "../../../context/AuthContext";
import { formatFriendlyDate } from "../../../utility/funcs";
import {
  BLOCK_REASONS,
  blockClient,
  blockReasonLabel,
  fetchClientBlock,
  unblockClient,
} from "../../../utility/clientBlocks";

export const UNBLOCK_PERMISSION = "clients.unblock";

function useClientBlock({ entityType, entityId, name, onChange }) {
  const { session, hasPermission } = UserAuth();
  const isCompany = entityType === "company";
  const noun = isCompany ? "la empresa" : "el cliente"; // mid-sentence
  const Noun = isCompany ? "Empresa" : "Cliente"; // titles and button labels

  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modal, setModal] = useState(null); // "block" | "unblock" | null
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");

  const load = useCallback(async () => {
    if (!entityId) {
      setState(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setState(await fetchClientBlock(entityType, entityId));
    setLoading(false);
  }, [entityType, entityId]);

  useEffect(() => {
    load();
  }, [load]);

  const blocked = !!state?.blocked;
  const canUnblock = hasPermission(UNBLOCK_PERMISSION);
  // A blocked client is frozen for everyone who can't undo the block.
  const readOnly = blocked && !canUnblock;

  const apply = (patch) => {
    setState((prev) => ({ ...prev, ...patch }));
    if (onChange) onChange(patch);
  };

  // "Otro" carries no meaning on its own — the detail *is* the motive there.
  const blockReady = !!reason && (reason !== "otro" || !!notes.trim());

  const confirmBlock = async () => {
    if (!blockReady) return;
    setSaving(true);
    const { data, error } = await blockClient({
      entityType,
      entityId,
      reason,
      notes,
      userId: session?.user?.id,
    });
    setSaving(false);
    if (error) {
      alert("No se pudo bloquear: " + error);
      return;
    }
    apply(data);
    setModal(null);
  };

  const confirmUnblock = async () => {
    setSaving(true);
    const { data, error } = await unblockClient({ entityType, entityId });
    setSaving(false);
    if (error) {
      alert("No se pudo desbloquear: " + error);
      return;
    }
    apply(data);
    setModal(null);
  };

  const openBlockModal = () => {
    setReason("");
    setNotes("");
    setModal("block");
  };

  // Only offered while the client is clean — once blocked, the banner owns the
  // controls. Nothing to show at all on an unsaved record.
  const actionGroups =
    entityId && !loading && !blocked
      ? [
          {
            title: "Más acciones",
            actions: [
              {
                content: "Bloquear",
                destructive: true,
                onAction: openBlockModal,
              },
            ],
          },
        ]
      : undefined;

  const markup = (
    <>
      {blocked && (
        <div style={{ marginBottom: "1rem" }}>
          <Banner
            tone="critical"
            title={`${isCompany ? "Empresa bloqueada" : "Cliente bloqueado"} — ${blockReasonLabel(
              state.block_reason
            )}`}
          >
            <BlockStack gap="200">
              {state.block_notes && <Text>{state.block_notes}</Text>}
              <Text>
                No se pueden crear reservaciones nuevas para {noun}. Las
                reservaciones existentes no se modifican.
              </Text>
              {state.blocked_at && (
                <Text variant="bodySm" tone="subdued">
                  Bloqueado el {formatFriendlyDate(state.blocked_at)}
                </Text>
              )}
              {canUnblock ? (
                <InlineStack>
                  <Button onClick={() => setModal("unblock")}>
                    Desbloquear
                  </Button>
                </InlineStack>
              ) : (
                <Text variant="bodySm">
                  La ficha está bloqueada para edición. Solo un usuario
                  autorizado puede desbloquearla o modificarla.
                </Text>
              )}
            </BlockStack>
          </Banner>
        </div>
      )}

      <Modal
        open={modal === "block"}
        onClose={() => setModal(null)}
        title={`Bloquear ${Noun.toLowerCase()}`}
        primaryAction={{
          content: "Bloquear",
          destructive: true,
          disabled: !blockReady || saving,
          loading: saving,
          onAction: confirmBlock,
        }}
        secondaryActions={[
          { content: "Cancelar", onAction: () => setModal(null) },
        ]}
      >
        <Modal.Section>
          <FormLayout>
            <Text>
              {name ? <b>{name}</b> : `${Noun}`} no podrá tener reservaciones
              nuevas, y su ficha quedará bloqueada para edición.
            </Text>
            <Select
              label="Motivo"
              requiredIndicator
              options={[
                { label: "Seleccionar motivo", value: "" },
                ...BLOCK_REASONS,
              ]}
              value={reason}
              onChange={setReason}
            />
            <TextField
              label="Detalle"
              value={notes}
              onChange={setNotes}
              multiline={3}
              autoComplete="off"
              placeholder="Qué pasó, número de reservación, etc."
              helpText="Se le muestra a quien intente crear la reservación."
              requiredIndicator={reason === "otro"}
            />
          </FormLayout>
        </Modal.Section>
      </Modal>

      <Modal
        open={modal === "unblock"}
        onClose={() => setModal(null)}
        title={`Desbloquear ${Noun.toLowerCase()}`}
        primaryAction={{
          content: "Desbloquear",
          disabled: saving,
          loading: saving,
          onAction: confirmUnblock,
        }}
        secondaryActions={[
          { content: "Cancelar", onAction: () => setModal(null) },
        ]}
      >
        <Modal.Section>
          <Text>
            Se quitará el bloqueo{name ? ` de ${name}` : ""} y el motivo
            registrado ({blockReasonLabel(state?.block_reason)}). Podrá volver a
            reservar y su ficha será editable de inmediato.
          </Text>
        </Modal.Section>
      </Modal>
    </>
  );

  return { loading, blocked, canUnblock, readOnly, actionGroups, markup, reload: load };
}

export default useClientBlock;
